import React, { createContext, useContext, useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';
import { X, Check, CheckCircle, AlertCircle, Info, AlertTriangle, Loader2, ChevronDown, Search, ChevronRight, type LucideIcon } from 'lucide-react';

/**
 * Configure tailwind-merge to recognize semantic font size aliases.
 * This prevents 'text-primary' from being treated as a 'text-color' and 
 * conflicting with 'text-white' or 'text-brand-command'.
 */
const customTwMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [
        'text-header-xl',
        'text-header',
        'text-group',
        'text-primary',
        'text-utility',
        'text-micro',
        'text-badge'
      ],
      'tracking': [
        'tracking-infrastructure',
        'tracking-tight-caps'
      ]
    }
  }
});

/**
 * Merges class names using clsx and a customized tailwind-merge.
 */
export function cn(...inputs: ClassValue[]) {
  return customTwMerge(clsx(inputs));
}

// --- PORTAL ---
export function Portal({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = useState(false);
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setMounted(true), []);
  return mounted ? createPortal(children, document.body) : null;
}

// --- CONTEXT & HOOKS ---
export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
}

interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'primary' | 'danger' | 'warning';
}

interface UIContextType {
  showToast: (message: string, type?: ToastType) => void;
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const UIContext = createContext<UIContextType | null>(null);

export function useUI() {
  const context = useContext(UIContext);
  if (!context) throw new Error('useUI must be used within a UIProvider');
  return context;
}

// --- MODAL ---
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl' | 'full';
  /** DL-601: while true, backdrop / Escape / X ask before discarding (content can also report via `useReportDirty`). */
  isDirty?: boolean;
}

// DL-601 (ADR-046 amendment), DL-602: lets a form report its own unsaved state to whatever hosts it: a Modal
// (which then guards its backdrop / Escape / X) or a DirtyReportProvider (e.g. the Registry room's edit pane, which
// feeds its room-leave and record-switch guards). Null with no host, where the hook does nothing.
const DirtyReportContext = createContext<((isDirty: boolean) => void) | null>(null);

export function useReportDirty(isDirty: boolean) {
  const report = useContext(DirtyReportContext);
  useEffect(() => {
    report?.(isDirty);
    return () => report?.(false);
  }, [report, isDirty]);
}

export function DirtyReportProvider({ onDirtyChange, children }: { onDirtyChange: (isDirty: boolean) => void; children: React.ReactNode }) {
  return <DirtyReportContext.Provider value={onDirtyChange}>{children}</DirtyReportContext.Provider>;
}

export function Modal({ isOpen, onClose, title, children, maxWidth = 'md', isDirty = false }: ModalProps) {
  const onCloseRef = useRef(onClose);
  useLayoutEffect(() => { onCloseRef.current = onClose; });

  // Read directly (not useUI) so a Modal outside a UIProvider still renders, just without the guard.
  const confirm = useContext(UIContext)?.confirm;
  const [contentDirty, setContentDirty] = useState(false);
  const dirtyRef = useRef(false);
  useLayoutEffect(() => { dirtyRef.current = isDirty || contentDirty; });
  // Escape reaches both this Modal and the confirm dialog stacked over it; one prompt at a time.
  const promptingRef = useRef(false);

  // Backdrop, Escape and X: dismissals that could be a slip. Explicit Cancel/Save buttons call onClose themselves.
  const requestClose = useCallback(async () => {
    if (promptingRef.current) return;
    if (dirtyRef.current && confirm) {
      promptingRef.current = true;
      const discard = await confirm({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Discard them and close?',
        confirmLabel: 'Discard',
        cancelLabel: 'Keep Editing',
        variant: 'danger',
      });
      promptingRef.current = false;
      if (!discard) return;
    }
    onCloseRef.current();
  }, [confirm]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') requestClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, requestClose]);

  if (!isOpen) return null;

  const maxW = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '4xl': 'max-w-4xl',
    full: 'max-w-[95vw]',
  };

  return (
    <Portal>
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6">
        <div className="absolute inset-0 bg-brand-command/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={requestClose} />
        <div className={cn(
          "relative w-full bg-brand-surface rounded-xl shadow-precision animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh] border border-brand-edge",
          maxW[maxWidth]
        )}>
          {title ? (
            <div className="px-6 h-14 border-b border-brand-edge flex items-center justify-between shrink-0 bg-brand-surface">
              <h3 className="text-group font-bold uppercase tracking-tight text-brand-command">{title}</h3>
              <button onClick={requestClose} className="p-1.5 hover:bg-brand-edge rounded-lg transition-colors text-brand-label hover:text-brand-command">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button 
              onClick={requestClose} 
              className="absolute top-4 right-4 z-[210] p-1.5 bg-brand-surface/80 backdrop-blur-sm hover:bg-brand-surface rounded-lg transition-colors text-brand-label hover:text-brand-command shadow-hairline border border-brand-edge"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <div className="flex-1 overflow-y-auto p-6 text-brand-label">
            <DirtyReportContext.Provider value={setContentDirty}>
              {children}
            </DirtyReportContext.Provider>
          </div>
        </div>
      </div>
    </Portal>
  );
}

// --- MODAL FRAME ---
// A bare sized backdrop+frame for modals whose child owns its own header/close control
// (e.g. WavePacker, PackingDesk). Deliberately has no onClose/Escape/backdrop-click
// behavior — it only replaces duplicated structural markup, not interaction.
export function ModalFrame({ width, height = "h-[85vh] max-h-[800px]", children }: { width: string; height?: string; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-brand-command/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className={cn(width, height, "bg-brand-canvas rounded-xl shadow-precision border border-brand-edge overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col")}>
        {children}
      </div>
    </div>
  );
}

// --- PROVIDER ---
export function UIProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [confirmState, setConfirmState] = useState<{ options: ConfirmOptions; resolve: (val: boolean) => void } | null>(null);

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000);
  }, []);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>(resolve => {
      setConfirmState({ options, resolve });
    });
  }, []);

  const handleConfirm = (value: boolean) => {
    if (confirmState) {
      confirmState.resolve(value);
      setConfirmState(null);
    }
  };

  return (
    <UIContext.Provider value={{ showToast, confirm }}>
      {children}
      
      {/* Toast Container */}
      <div className="fixed bottom-6 right-6 z-[300] flex flex-col space-y-3">
        {toasts.map(toast => (
          <div 
            key={toast.id} 
            className={cn(
              "flex items-center space-x-3 px-6 py-3 rounded-xl shadow-2xl border-2 animate-in slide-in-from-right-10 duration-300",
              toast.type === 'success' && "bg-success-surface border-success-edge text-success",
              toast.type === 'error' && "bg-danger-surface border-danger-edge text-danger",
              toast.type === 'warning' && "bg-warning-surface border-warning-edge text-warning",
              toast.type === 'info' && "bg-brand-surface border-brand-edge text-brand-command"
            )}
          >
            {toast.type === 'success' && <CheckCircle className="w-4 h-4 text-success" />}
            {toast.type === 'error' && <AlertCircle className="w-4 h-4 text-danger" />}
            {toast.type === 'warning' && <AlertTriangle className="w-4 h-4 text-warning" />}
            {toast.type === 'info' && <Info className="w-4 h-4 text-brand-command" />}
            <p className="text-utility font-bold uppercase tracking-tight">{toast.message}</p>
          </div>
        ))}
      </div>

      {/* Confirm Modal */}
      {confirmState && (
        <Modal 
          isOpen={true} 
          onClose={() => handleConfirm(false)} 
          title={confirmState.options.title}
          maxWidth="sm"
        >
          <div className="space-y-6">
            <p className="text-utility font-semibold text-brand-label leading-relaxed">
              {confirmState.options.message}
            </p>
            <div className="flex justify-end space-x-3">
              <Button variant="ghost" onClick={() => handleConfirm(false)}>
                {confirmState.options.cancelLabel || 'Cancel'}
              </Button>
              <Button 
                variant={confirmState.options.variant === 'danger' ? 'danger' : 'primary'} 
                onClick={() => handleConfirm(true)}
              >
                {confirmState.options.confirmLabel || 'Confirm'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </UIContext.Provider>
  );
}

// --- BASIC UI COMPONENTS ---

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'outline' | 'ghost' | 'link' | 'active';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  loading?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  isLoading,
  loading,
  className,
  children,
  disabled,
  ...props
}: ButtonProps) {
  const isCurrentlyLoading = isLoading || loading;
  
  const variants = {
    primary: "bg-brand-command text-white border-b-2 border-brand-command hover:opacity-90 shadow-hairline",
    secondary: "bg-brand-secondary text-white border-b-2 border-brand-secondary hover:opacity-90 shadow-hairline",
    success: "bg-success text-white border-b-2 border-success hover:bg-success shadow-hairline",
    warning: "bg-warning text-white border-b-2 border-warning hover:bg-warning shadow-hairline",
    danger: "bg-danger text-white border-b-2 border-danger hover:bg-danger shadow-hairline",
    outline: "bg-brand-surface border-b-2 border-brand-edge text-brand-label hover:border-brand-command hover:text-brand-command shadow-hairline",
    ghost: "bg-transparent border-b-2 border-transparent text-brand-label hover:bg-brand-surface hover:text-brand-command",
    link: "bg-transparent text-brand-command hover:underline px-0 py-0 h-auto border-none shadow-none active:translate-y-0",
    active: "bg-brand-surface border-b-2 border-brand-command text-brand-command font-bold"
  };

  const sizes = {
    xs: "h-[26px] px-2 text-micro font-bold uppercase tracking-tight-caps rounded-lg shadow-precision",
    sm: "h-[32px] px-3 text-badge font-bold uppercase tracking-tight rounded-lg shadow-precision",
    md: "h-[36px] px-4 text-utility font-bold uppercase tracking-tight rounded-lg shadow-precision",
    lg: "h-[48px] px-6 text-primary font-bold uppercase tracking-tight rounded-xl shadow-precision"
  };

  return (
    <button
      className={cn(
        "inline-flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed active:translate-y-[1px] active:border-b-0",
        variants[variant],
        sizes[size],
        className
      )}
      disabled={disabled || isCurrentlyLoading}
      {...props}
    >
      {isCurrentlyLoading && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />}
      {children}
    </button>
  );
}

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  variant?: 'primary' | 'filter';
  hideLabelOnDesktop?: boolean;
}

export function Input({ label, error, className, variant = 'primary', hideLabelOnDesktop, ...props }: InputProps) {
  const variants = {
    primary: "px-4 h-9 bg-brand-surface border border-brand-edge rounded-lg text-primary font-bold text-brand-command transition-all focus:bg-brand-surface focus:border-brand-command focus:ring-2 focus:ring-brand-command outline-none placeholder:text-brand-label/40 placeholder:font-medium shadow-hairline",
    filter: "h-[26px] py-0 px-3 bg-brand-surface border border-brand-edge rounded-lg text-micro uppercase tracking-infrastructure text-brand-label focus:bg-brand-surface focus:border-brand-label focus:ring-0 shadow-hairline transition-all"
  };

  return (
    <div className={cn("w-full text-left", className)}>
      {label && (
        <label 
          className={cn(
            "block font-bold text-brand-label uppercase tracking-infrastructure ml-1",
            variant === 'filter' ? "text-micro mb-1" : "text-micro mb-1.5",
            hideLabelOnDesktop && "md:hidden"
          )}
        >
          {label}
        </label>
      )}
      <input
        aria-invalid={error ? true : undefined}
        className={cn(
          "w-full transition-all outline-none",
          variants[variant],
          variant === 'filter' && "h-[26px] py-0 px-3",
          variant === 'primary' && "h-9 py-0",
          error && "border-danger bg-danger-surface focus:border-danger",
          className
        )}
        {...props}
      />
      {error && <p className="mt-1.5 text-micro font-bold text-danger uppercase tracking-tight-caps ml-1">{error}</p>}
    </div>
  );
}

// --- SEARCH FIELD ---
interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  variant?: 'primary' | 'filter';
  className?: string;
  inputClassName?: string;
  iconSize?: 'xs' | 'sm';
}

export function SearchField({ value, onChange, placeholder, variant = 'primary', className, inputClassName, iconSize = 'sm' }: SearchFieldProps) {
  return (
    <div className={cn("relative group", className)}>
      <Search className={cn(
        "absolute left-3 top-1/2 -translate-y-1/2 text-brand-label group-focus-within:text-brand-command transition-colors z-10",
        iconSize === 'xs' ? "w-3 h-3" : "w-3.5 h-3.5"
      )} />
      <Input
        variant={variant}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn("pl-9", value && "pr-7", inputClassName)}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-brand-label hover:text-brand-command transition-colors"
          aria-label="Clear search"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

export function Textarea({ label, error, className, ...props }: TextareaProps) {
  return (
    <div className={cn("w-full text-left", className)}>
      {label && (
        <label className="block font-bold text-brand-label uppercase tracking-infrastructure ml-1 text-micro mb-1.5">
          {label}
        </label>
      )}
      <textarea
        aria-invalid={error ? true : undefined}
        className={cn(
          "w-full min-h-[6rem] py-2 transition-all outline-none",
          "px-4 bg-brand-surface border border-brand-edge rounded-lg text-primary font-bold text-brand-command",
          "focus:bg-brand-surface focus:border-brand-command focus:ring-2 focus:ring-brand-command",
          "placeholder:text-brand-label/40 placeholder:font-medium shadow-hairline",
          error && "border-danger bg-danger-surface focus:border-danger"
        )}
        {...props}
      />
      {error && <p className="mt-1.5 text-micro font-bold text-danger uppercase tracking-tight-caps ml-1">{error}</p>}
    </div>
  );
}

// DL-414: Select's and Combobox's portal dropdowns both anchor with `position: fixed` off the
// trigger's own getBoundingClientRect(), unconditionally placing the panel below the trigger.
// With no viewport-space check, a trigger in the lower portion of a shorter viewport (a laptop
// screen, a long form, a mobile viewport) renders its options past the bottom edge — a `fixed`
// element that scrolling cannot bring into view, since it's already viewport-relative. Flips to
// anchoring above the trigger (via `bottom` instead of `top`, sidestepping the need to know the
// panel's real height before it renders) whenever there's more room above than below and not
// enough room below to fit the shared `max-h-[300px]` cap both panels already render with.
//
// DL-421: the same `left: rect.left` was never checked against the right edge either — a
// trigger near a narrow column's edge (e.g. Requests, squeezed between the nav rail and
// the fixed inventory panel) could render its panel partly off-page. `panelWidth` lets each
// caller clamp against its own actual rendered width. Coords are also now recomputed on resize
// and scroll (see `useRecomputeOnReposition` below) — previously they were computed once at
// click-time and went stale if the trigger moved under the user without the panel closing.
const DROPDOWN_MAX_HEIGHT = 300;
const DROPDOWN_EDGE_MARGIN = 8;

export function computeDropdownCoords(rect: DOMRect, panelWidth: number): { left: number; width: number; top?: number; bottom?: number } {
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const flipUp = spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow;
  const left = Math.min(
    Math.max(rect.left, DROPDOWN_EDGE_MARGIN),
    Math.max(window.innerWidth - panelWidth - DROPDOWN_EDGE_MARGIN, DROPDOWN_EDGE_MARGIN)
  );
  return flipUp
    ? { left, width: rect.width, bottom: window.innerHeight - rect.top + 4 }
    : { left, width: rect.width, top: rect.bottom + 4 };
}

// Keeps an open dropdown's position honest across a live window resize (DL-421) or a scroll —
// without this, coords computed once at click-time go stale the instant the trigger moves
// under the user, leaving the fixed-position portal panel visually detached from its field.
// The scroll listener is capture-phase so it catches scrolling on any ancestor scrollable
// container, not just the window (scroll events don't bubble).
export function useRecomputeOnReposition(isOpen: boolean, recompute: () => void) {
  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('resize', recompute);
    document.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      document.removeEventListener('scroll', recompute, true);
    };
  }, [isOpen, recompute]);
}

// ADR-124: shared keyboard-nav behavior for every portal-dropdown widget (Select, MultiSelect,
// Combobox, SizeSelect) — virtual focus (real DOM focus stays on the trigger/input; the
// highlighted option is tracked here and rendered by the caller with its own hover-matching
// style). ArrowDown/Up move the highlight (clamped, not cyclic); Home/End jump to first/last;
// Enter commits via onSelectIndex (the caller decides whether that also closes — MultiSelect's
// doesn't); Escape closes via onClose. Deliberately does not handle Enter/Space while closed —
// that already opens the trigger via native <button> behavior and needs no reimplementation.
// No internal effect resets highlightedIndex on open — the caller sets it explicitly (alongside
// its own setIsOpen(true)) at every point it opens, mouse or keyboard, since "the right index to
// start highlighted at" is caller-specific (e.g. the current value's position).
export function useDropdownKeyboardNav({
  isOpen, itemCount, onSelectIndex, onOpen, onClose,
}: {
  isOpen: boolean;
  itemCount: number;
  onSelectIndex: (index: number) => void;
  onOpen: () => void;
  onClose: () => void;
}) {
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        onOpen();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(i => Math.min(i + 1, itemCount - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(i => Math.max(i - 1, 0));
        break;
      case 'Home':
        e.preventDefault();
        setHighlightedIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setHighlightedIndex(Math.max(itemCount - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < itemCount) onSelectIndex(highlightedIndex);
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  };

  return { highlightedIndex, setHighlightedIndex, handleKeyDown };
}

export interface SelectProps {
  value: string | number | undefined;
  onChange: (value: string) => void;
  onBlur?: () => void;
  options: { label: string; value: string | number }[];
  placeholder?: string;
  label?: string;
  error?: string;
  variant?: 'primary' | 'ghost' | 'filter' | 'badge';
  className?: string;
  disabled?: boolean;
  hideLabelOnDesktop?: boolean;
}

export function Select({ value, onChange, onBlur, options, placeholder = "Select...", error, variant = 'primary', className, label, disabled, hideLabelOnDesktop }: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{ left: number; width: number; top?: number; bottom?: number }>({ top: 0, left: 0, width: 0 });
  const containerRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const optionRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const reactId = React.useId();

  const recompute = useCallback(() => {
    if (containerRef.current) {
      const panelWidth = Math.max(containerRef.current.getBoundingClientRect().width, 200);
      setCoords(computeDropdownCoords(containerRef.current.getBoundingClientRect(), panelWidth));
    }
  }, []);
  useRecomputeOnReposition(isOpen, recompute);

  const normalize = (v: any) => String(v || '').trim().toLowerCase();
  const selectedIndex = options.findIndex(opt => normalize(opt.value) === normalize(value));

  // Plain function declarations (not const arrow fns) so they're hoisted — open/close are
  // referenced inside useDropdownKeyboardNav's onOpen/onClose below, which is declared before
  // these, and setHighlightedIndex (from that same hook call) is referenced inside open() here.
  // Both are only ever *called* after the whole component body has finished this render pass,
  // so the circular textual reference is safe; hoisting just keeps it lint-clean either order.
  function open() {
    if (disabled) return;
    recompute();
    setHighlightedIndex(Math.max(selectedIndex, 0));
    setIsOpen(true);
  }
  function close() { setIsOpen(false); }
  function toggle() { if (isOpen) close(); else open(); }

  const { highlightedIndex, setHighlightedIndex, handleKeyDown } = useDropdownKeyboardNav({
    isOpen,
    itemCount: options.length,
    onOpen: open,
    onClose: () => { close(); onBlur?.(); },
    onSelectIndex: (index) => {
      const opt = options[index];
      if (opt) onChange(String(opt.value));
      close();
    },
  });

  useEffect(() => {
    if (isOpen) optionRefs.current[highlightedIndex]?.scrollIntoView?.({ block: 'nearest' });
  }, [isOpen, highlightedIndex]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen &&
          containerRef.current && !containerRef.current.contains(event.target as Node) &&
          contentRef.current && !contentRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        onBlur?.();
      }
    };

    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.body.style.overflow = originalStyle;
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onBlur]);

  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const variants = {
    // h-9 matches Input's primary variant exactly — these render as sibling fields in the same
    // form grid and must be the same height. py-0 (not py-3) lets flex centering own the
    // vertical position instead of padding fighting the fixed height.
    primary: "px-4 h-9 py-0 bg-brand-surface border border-brand-edge rounded-xl text-primary font-bold text-brand-command transition-all focus:bg-brand-surface focus:border-brand-command shadow-hairline",
    ghost: "bg-transparent border-none px-2 h-[26px] text-micro uppercase tracking-infrastructure text-brand-label hover:bg-brand-surface rounded-md",
    badge: "bg-brand-surface border border-brand-edge px-2 h-[22px] text-badge uppercase tracking-infrastructure text-brand-command hover:bg-brand-surface rounded-sm shadow-hairline",
    filter: "h-[26px] bg-brand-surface border border-brand-edge px-3 text-micro uppercase tracking-infrastructure text-brand-label hover:bg-brand-surface hover:border-brand-label hover:shadow-hairline rounded-lg transition-all"
  };

  return (
    <div className={cn("w-full text-left relative", className)} ref={containerRef}>
      {label && (
        <label
          className={cn(
            "block font-bold text-brand-label uppercase tracking-infrastructure ml-1",
            variant === 'filter' ? "text-micro mb-1" : "text-micro mb-1.5",
            hideLabelOnDesktop && "md:hidden"
          )}
        >
          {label}
        </label>
      )}

      <button
        type="button"
        onClick={toggle}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-invalid={error ? true : undefined}
        aria-expanded={isOpen}
        aria-activedescendant={isOpen ? `${reactId}-opt-${highlightedIndex}` : undefined}
        className={cn(
          "flex items-center justify-between w-full outline-none",
          variants[variant],
          error && "border-danger bg-danger-surface",
          isOpen && variant === 'primary' && "border-brand-command bg-brand-surface ring-2 ring-brand-command",
          disabled && "opacity-50 cursor-not-allowed",
          // Force layout properties for specific variants to prevent local overrides
          variant === 'filter' && "h-[26px] py-0 px-3",
          variant === 'ghost' && "h-[26px] py-0 px-2",
          variant === 'badge' && "h-[22px] py-0 px-2"
        )}
      >
        <span
          className={cn(
            "truncate mr-2",
            variant === 'badge' && "text-badge",
            variant === 'filter' && "text-micro",
            variant === 'primary' && "text-primary",
            !selectedOption && !value && "text-brand-label/40 font-medium italic normal-case"
          )}
        >
          {selectedOption ? selectedOption.label : (value ? String(value) : placeholder)}
        </span>
        <ChevronDown size={variant === 'primary' ? 16 : 10} className={cn("text-brand-label/40 transition-transform shrink-0", isOpen && "rotate-180")} />
      </button>

      {isOpen && (
        <Portal>
          <div
            ref={contentRef}
            role="listbox"
            id={`${reactId}-listbox`}
            className="fixed z-[300] bg-brand-surface border border-brand-edge rounded-xl shadow-precision overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[300px]"
            style={{
              ...(coords.top !== undefined ? { top: coords.top } : { bottom: coords.bottom }),
              left: coords.left,
              width: Math.max(coords.width, 200)
            }}
          >
            <div className="overflow-y-auto py-1 max-h-[290px] scrollbar-thin">
              {options.length === 0 ? (
                <div className="px-4 py-3 text-micro text-brand-label font-bold uppercase tracking-tight italic">No options</div>
              ) : options.map((opt, index) => (
                <button
                  key={String(opt.value)}
                  id={`${reactId}-opt-${index}`}
                  role="option"
                  aria-selected={String(value) === String(opt.value)}
                  ref={(el) => { optionRefs.current[index] = el; }}
                  type="button"
                  className={cn(
                    "w-full text-left px-4 py-2 transition-colors flex items-center justify-between group",
                    (String(value) === String(opt.value) || index === highlightedIndex)
                      ? "bg-brand-canvas text-brand-command"
                      : "text-brand-label hover:bg-brand-canvas hover:text-brand-command"
                  )}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => {
                    onChange(String(opt.value));
                    close();
                  }}
                >
                  <span className={cn(
                    "uppercase tracking-infrastructure text-badge font-bold"
                  )}>{opt.label}</span>
                  {String(value) === String(opt.value) && <CheckCircle className="w-3.5 h-3.5 text-brand-command" />}
                </button>
              ))}
            </div>
          </div>
        </Portal>
      )}
      
      {error && <p className="mt-1.5 text-badge font-bold text-danger uppercase tracking-tight-caps ml-1">{error}</p>}
    </div>
  );
}

// --- MULTI-SELECT (CHECKBOX DROPDOWN) ---
export interface MultiSelectProps {
  label: string;
  options: { label: string; value: string }[];
  selected: string[];
  onChange: (vals: string[]) => void;
  placeholder?: string;
  className?: string;
}

// Portal + computeDropdownCoords (shared with Select/Combobox above) so this dropdown
// never clips against a scrolling/overflow-hidden parent — the bug class this
// component's predecessor (an inline flex-wrap checkbox row) kept running into.
const MULTISELECT_PANEL_WIDTH = 192; // w-48

export function MultiSelect({ label, options, selected, onChange, placeholder = "Select...", className }: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{ left: number; width: number; top?: number; bottom?: number }>({ top: 0, left: 0, width: 0 });
  const containerRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const optionRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const reactId = React.useId();

  const recompute = useCallback(() => {
    if (containerRef.current) {
      setCoords(computeDropdownCoords(containerRef.current.getBoundingClientRect(), MULTISELECT_PANEL_WIDTH));
    }
  }, []);
  useRecomputeOnReposition(isOpen, recompute);

  const toggleValue = (val: string) => {
    onChange(selected.includes(val) ? selected.filter(s => s !== val) : [...selected, val]);
  };

  function open() {
    recompute();
    setHighlightedIndex(0);
    setIsOpen(true);
  }
  function close() { setIsOpen(false); }
  function toggleOpen() { if (isOpen) close(); else open(); }

  // Enter toggles the highlighted checkbox but deliberately does not close — matches this
  // component's existing click behavior (multi-pick, Clear All to reset).
  const { highlightedIndex, setHighlightedIndex, handleKeyDown } = useDropdownKeyboardNav({
    isOpen,
    itemCount: options.length,
    onOpen: open,
    onClose: close,
    onSelectIndex: (index) => { const opt = options[index]; if (opt) toggleValue(opt.value); },
  });

  useEffect(() => {
    if (isOpen) optionRefs.current[highlightedIndex]?.scrollIntoView?.({ block: 'nearest' });
  }, [isOpen, highlightedIndex]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen &&
          containerRef.current && !containerRef.current.contains(event.target as Node) &&
          contentRef.current && !contentRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className={cn("relative", className)} ref={containerRef}>
      <button
        type="button"
        onClick={toggleOpen}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-activedescendant={isOpen ? `${reactId}-opt-${highlightedIndex}` : undefined}
        className={cn(
          "flex items-center justify-between h-[26px] px-3 w-full bg-brand-surface border border-brand-edge rounded-lg text-micro font-bold uppercase tracking-infrastructure text-brand-label hover:border-brand-label hover:shadow-hairline transition-all min-w-[120px]",
          selected.length > 0 && "border-brand-command text-brand-command"
        )}
      >
        <span className="truncate mr-2">
          {selected.length === 0 ? label : `${label} (${selected.length})`}
        </span>
        <ChevronDown size={10} className={cn("text-brand-label/40 transition-transform shrink-0", isOpen && "rotate-180")} />
      </button>

      {isOpen && (
        <Portal>
          <div
            ref={contentRef}
            role="listbox"
            aria-multiselectable="true"
            className="fixed z-[300] bg-brand-surface border border-brand-edge rounded-xl shadow-precision overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[300px] w-48"
            style={{
              ...(coords.top !== undefined ? { top: coords.top } : { bottom: coords.bottom }),
              left: coords.left,
            }}
          >
            <div className="px-3 py-1.5 border-b border-brand-canvas">
              <span className="text-micro font-bold text-brand-label uppercase tracking-infrastructure">{placeholder}</span>
            </div>
            <div className="overflow-y-auto py-1 max-h-[230px] scrollbar-thin">
              {options.map((opt, index) => (
                <button
                  key={opt.value}
                  id={`${reactId}-opt-${index}`}
                  role="option"
                  aria-selected={selected.includes(opt.value)}
                  ref={(el) => { optionRefs.current[index] = el; }}
                  type="button"
                  onClick={() => toggleValue(opt.value)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-1.5 transition-colors group",
                    index === highlightedIndex ? "bg-brand-canvas" : "hover:bg-brand-canvas"
                  )}
                >
                  <span className={cn(
                    "w-3.5 h-3.5 rounded-sm border flex items-center justify-center shrink-0 transition-colors",
                    selected.includes(opt.value) ? "bg-brand-command border-brand-command" : "border-brand-edge-strong bg-brand-surface group-hover:border-brand-command"
                  )}>
                    {selected.includes(opt.value) && <Check size={10} className="text-white" strokeWidth={3} />}
                  </span>
                  <span className={cn(
                    "text-badge font-bold uppercase tracking-tight",
                    selected.includes(opt.value) ? "text-brand-command" : "text-brand-label"
                  )}>{opt.label}</span>
                </button>
              ))}
            </div>
            {selected.length > 0 && (
              <div className="px-2 py-2 border-t border-brand-canvas">
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="w-full py-1 text-micro font-bold uppercase text-danger hover:bg-danger-surface rounded transition-all"
                >
                  Clear All
                </button>
              </div>
            )}
          </div>
        </Portal>
      )}
    </div>
  );
}

// --- COMBOBOX (SEARCHABLE SELECT) ---
export interface ComboboxProps extends SelectProps {
  onSearchChange?: (val: string) => void;
}

export function Combobox({ value, onChange, options, placeholder = "Search...", error, variant = 'primary', className, label, disabled }: ComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [coords, setCoords] = useState<{ left: number; width: number; top?: number; bottom?: number }>({ top: 0, left: 0, width: 0 });
  const containerRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const optionRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const reactId = React.useId();

  const recompute = useCallback(() => {
    if (containerRef.current) {
      const panelWidth = Math.max(containerRef.current.getBoundingClientRect().width, 240);
      setCoords(computeDropdownCoords(containerRef.current.getBoundingClientRect(), panelWidth));
    }
  }, []);
  useRecomputeOnReposition(isOpen, recompute);

  const normalize = (v: any) => String(v || '').trim().toLowerCase();
  const selectedOption = options.find(opt => normalize(opt.value) === normalize(value));

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(search.toLowerCase()) ||
    normalize(opt.value).includes(search.toLowerCase())
  );

  function open() {
    if (disabled) return;
    recompute();
    setSearch('');
    setHighlightedIndex(0);
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  }
  function close() {
    setIsOpen(false);
    triggerRef.current?.focus();
  }
  function toggle() { if (isOpen) close(); else open(); }

  // Real focus lives on the search <input> while open (existing behavior — it's focused right
  // after opening), not the trigger button, so the same handleKeyDown is wired onto both: the
  // button's closed-state ArrowDown/Up-to-open branch, and the input's open-state nav branch.
  const { highlightedIndex, setHighlightedIndex, handleKeyDown } = useDropdownKeyboardNav({
    isOpen,
    itemCount: filteredOptions.length,
    onOpen: open,
    onClose: close,
    onSelectIndex: (index) => {
      const opt = filteredOptions[index];
      if (opt) onChange(String(opt.value));
      close();
    },
  });

  useEffect(() => {
    if (isOpen) optionRefs.current[highlightedIndex]?.scrollIntoView?.({ block: 'nearest' });
  }, [isOpen, highlightedIndex]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && 
          containerRef.current && !containerRef.current.contains(event.target as Node) &&
          contentRef.current && !contentRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.body.style.overflow = originalStyle;
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const variants = {
    // h-9 matches Input's primary variant exactly (see Select's identical fix, same session).
    primary: "px-4 h-9 py-0 bg-brand-surface border border-brand-edge rounded-xl text-primary font-bold text-brand-command transition-all focus:bg-brand-surface focus:border-brand-command shadow-hairline",
    ghost: "bg-transparent border-none px-2 h-[26px] text-micro uppercase tracking-infrastructure text-brand-label hover:bg-brand-surface rounded-md",
    badge: "bg-brand-surface border border-brand-edge px-2 h-[22px] text-badge uppercase tracking-infrastructure text-brand-command hover:bg-brand-surface rounded-sm shadow-hairline",
    filter: "h-[26px] bg-brand-surface border border-brand-edge px-3 text-micro uppercase tracking-infrastructure text-brand-label hover:bg-brand-surface hover:border-brand-label hover:shadow-hairline rounded-lg transition-all"
  };

  return (
    <div className={cn("w-full text-left relative", className)} ref={containerRef}>
      {label && (
        <label 
          className={cn(
            "block font-bold text-brand-label uppercase tracking-infrastructure ml-1",
            variant === 'filter' ? "text-micro mb-1" : "text-badge mb-1.5"
          )}
        >
          {label}
        </label>
      )}
      
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-invalid={error ? true : undefined}
        aria-expanded={isOpen}
        className={cn(
          "flex items-center justify-between w-full outline-none",
          variants[variant],
          error && "border-danger bg-danger-surface",
          isOpen && variant === 'primary' && "border-brand-command bg-brand-surface ring-2 ring-brand-command",
          disabled && "opacity-60 cursor-not-allowed text-left",
          // Force layout properties for specific variants to prevent local overrides
          variant === 'filter' && "h-[26px] py-0 px-3",
          variant === 'ghost' && "h-[26px] py-0 px-2",
          variant === 'badge' && "h-[22px] py-0 px-2"
        )}
      >
        <div className="flex items-center space-x-2 truncate overflow-hidden">
          <Search size={16} className="text-brand-label/40 shrink-0" />
          <span className={cn(
            "truncate mr-2",
            variant === 'badge' && "text-badge",
            variant === 'filter' && "text-micro",
            variant === 'primary' && "text-primary",
            !selectedOption && "text-brand-label/40 font-medium italic normal-case"
          )}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown size={16} className={cn("text-brand-label/40 transition-transform shrink-0 ml-2", isOpen && "rotate-180")} />
      </button>

      {isOpen && (
        <Portal>
          <div
            ref={contentRef}
            className="fixed z-[300] bg-brand-surface border border-brand-edge rounded-xl shadow-precision overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[300px]"
            style={{
              ...(coords.top !== undefined ? { top: coords.top } : { bottom: coords.bottom }),
              left: coords.left,
              width: Math.max(coords.width, 240)
            }}
          >
            <div className="p-2 border-b border-brand-canvas shrink-0 bg-brand-canvas/50">
              <input
                ref={inputRef}
                type="text"
                role="combobox"
                aria-expanded="true"
                aria-controls={`${reactId}-listbox`}
                aria-activedescendant={filteredOptions.length > 0 ? `${reactId}-opt-${highlightedIndex}` : undefined}
                className="w-full px-4 py-2 text-badge font-bold uppercase tracking-tight bg-brand-surface border border-brand-edge rounded-xl focus:ring-0 focus:border-brand-command placeholder:text-brand-label/40"
                placeholder="Search..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setHighlightedIndex(0); }}
                onKeyDown={handleKeyDown}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div id={`${reactId}-listbox`} role="listbox" className="overflow-y-auto py-1 flex-1 scrollbar-thin">
              {filteredOptions.length === 0 ? (
                <div className="px-4 py-3 text-micro text-brand-label font-bold uppercase tracking-tight italic text-center">No matches</div>
              ) : filteredOptions.map((opt, index) => (
                <button
                  key={String(opt.value)}
                  id={`${reactId}-opt-${index}`}
                  role="option"
                  aria-selected={String(value) === String(opt.value)}
                  ref={(el) => { optionRefs.current[index] = el; }}
                  type="button"
                  className={cn(
                    "w-full text-left px-4 py-2 transition-colors flex items-center justify-between group",
                    (String(value) === String(opt.value) || index === highlightedIndex)
                      ? "bg-brand-canvas text-brand-command font-bold"
                      : "text-brand-label hover:bg-brand-canvas hover:text-brand-command"
                  )}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => {
                    onChange(String(opt.value));
                    close();
                  }}
                >
                  <div className="flex flex-col overflow-hidden text-left">
                    <span className={cn(
                      "uppercase tracking-infrastructure text-badge font-bold"
                    )}>{opt.label}</span>
                    {opt.value && <span className="text-badge text-brand-label font-mono tracking-tighter">{String(opt.value)}</span>}
                  </div>
                  {String(value) === String(opt.value) && <CheckCircle className="w-3.5 h-3.5 text-brand-command shrink-0 ml-2" />}
                </button>
              ))}
            </div>
          </div>
        </Portal>
      )}
      
      {error && <p className="mt-1.5 text-badge font-bold text-danger uppercase tracking-tight-caps ml-1">{error}</p>}
    </div>
  );
}

// --- BADGE ---
export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'outline';
}

export function Badge({ 
  children, 
  className, 
  variant = 'primary', 
  ...props 
}: BadgeProps) {
  const variants = {
    primary: 'bg-brand-surface text-brand-command border-brand-edge',
    secondary: 'bg-brand-canvas text-brand-command border-brand-edge',
    success: 'bg-success-surface text-success border-success-edge',
    danger: 'bg-danger-surface text-danger border-danger-edge',
    warning: 'bg-warning-surface text-warning border-warning-edge',
    info: 'bg-info-surface text-info border-info-edge',
    outline: 'bg-transparent border border-brand-edge text-brand-label',
  };

  return (
    <div
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-[1px] text-badge font-bold uppercase tracking-infrastructure border shrink-0 leading-tight',
        variants[variant],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// --- CARD ---
export type CardProps = React.HTMLAttributes<HTMLDivElement>;

export function Card({ children, className, ...props }: CardProps) {
  return (
    <div
      className={cn('bg-brand-surface rounded-xl border border-brand-edge shadow-hairline overflow-hidden', className)}
      {...props}
    >
      {children}
    </div>
  );
}

// --- ACTION CARD ---
export interface ActionCardProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: LucideIcon;
  label?: string;
  title: string;
  subtitle?: string;
  indicatorColor?: string;
}

export function ActionCard({ 
  icon: Icon, 
  label, 
  title, 
  subtitle, 
  indicatorColor,
  className,
  ...props 
}: ActionCardProps) {
  return (
    <button
      type="button"
      className={cn(
        "bg-brand-surface rounded-xl p-4 border border-brand-edge transition-all group text-left relative overflow-hidden",
        "hover:border-brand-command hover:bg-brand-surface hover:shadow-md active:translate-y-[1px]",
        props.disabled && "opacity-60 cursor-not-allowed grayscale",
        className
      )}
      {...props}
    >
      <div className="flex items-center justify-between mb-2">
        {Icon ? (
          <Icon size={14} className="text-brand-label group-hover:text-brand-command transition-colors" />
        ) : <div className="w-4 h-4" />}
        {label && <span className="text-micro font-bold text-brand-label uppercase opacity-60">{label}</span>}
      </div>
      
      <p className="text-primary font-bold text-brand-command truncate block pr-6">
        {title}
      </p>
      
      {(subtitle || indicatorColor) && (
        <div className="flex items-center gap-1.5 mt-1">
          {indicatorColor && (
            <span className={cn("w-1.5 h-1.5 rounded-full animate-pulse", indicatorColor)} />
          )}
          {subtitle && (
            <span className="text-micro font-bold text-brand-label uppercase opacity-60">
              {subtitle}
            </span>
          )}
        </div>
      )}

      {/* Touch Affordance: Chevron */}
      <div className="absolute top-1/2 -translate-y-1/2 right-3 text-brand-edge group-hover:text-brand-command transition-colors">
        <ChevronRight size={14} />
      </div>
    </button>
  );
}

// --- SEGMENTED CONTROL ---
interface SegmentedControlProps {
  options: { label: string; value: string }[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  /** Overrides the button height/sizing (default h-[24px]) — set explicitly, not derived from the container's height minus padding. */
  buttonClassName?: string;
}

export function SegmentedControl({ options, value, onChange, className, buttonClassName }: SegmentedControlProps) {
  return (
    <div className={cn("flex items-center p-1 bg-brand-surface border border-brand-edge rounded-lg", className)}>
      {options.map(opt => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={cn(
            "flex items-center px-3 h-[24px] rounded-lg text-micro font-bold uppercase tracking-infrastructure transition-all whitespace-nowrap",
            value === opt.value
              ? "bg-brand-secondary text-white shadow-hairline border border-brand-edge"
              : "text-brand-label hover:text-brand-command",
            buttonClassName
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// --- FILE INPUT ---
export interface FileInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  onFileSelect?: (file: File | null) => void;
}

export function FileInput({ label, error, className, onFileSelect, ...props }: FileInputProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (onFileSelect) onFileSelect(file);
  };

  return (
    <div className="w-full text-left">
      {label && (
        <label className="block text-micro font-bold text-brand-label uppercase tracking-tight mb-1.5 ml-1">
          {label}
        </label>
      )}
      <input
        type="file"
        aria-invalid={error ? true : undefined}
        className={cn(
          "h-9 w-full px-3 bg-brand-surface border border-brand-edge rounded-lg transition-all focus:bg-brand-surface focus:border-brand-command outline-none text-brand-command font-semibold file:mr-4 file:h-full file:px-3 file:rounded-md file:border-0 file:text-badge file:font-bold file:uppercase file:tracking-widest file:bg-brand-command file:text-white hover:file:bg-brand-command/90 text-utility",
          error && "border-danger bg-danger-surface focus:border-danger",
          className
        )}
        onChange={handleChange}
        {...props}
      />
      {error && <p className="mt-1.5 text-micro font-bold text-danger uppercase tracking-tight-caps ml-1">{error}</p>}
    </div>
  );
}

